import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { decamelize } from 'src/utils/decamelize';

import {
  denormalize,
  denormalizeToFormData,
  normalize,
} from '../shared/normalizers';

import { Capability } from './_types';

interface BulkUpdateParams {
  capabilities: {
    userId: Identifier;
    permission: number;
  }[];
  objectIds: Identifier[];
  objectType: 'exercise' | 'planWorkout' | 'workoutPlan';
}

export const bulkUpdateCapabilities = async (data: BulkUpdateParams) => {
  await api.post(
    '/api/v3/capabilities/bulk_update/',
    denormalizeToFormData({
      ...data,
      objectType: decamelize(data.objectType),
    })
  );
};

export const fetchCapabilities = async (
  params?: {
    exerciseId?: Identifier;
    workoutPlanId?: Identifier;
    authToken?: string;
  }
): Promise<Capability[]> => {
  const { authToken, ...capabilityParams } = params ?? {};
  const response = await api.get('/api/v3/capabilities', {
    params: {
      fetch_all: true,
      ...(authToken
        ? { auth_token: authToken, request_from_app: true }
        : {}),
      ...(capabilityParams && denormalize(capabilityParams)),
    },
  });
  return response.data.map(normalize);
};

export const deleteCapability = async (id: Identifier) => {
  try {
    await api.delete(`/api/v3/capabilities/${id}`);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Unable to delete capability.'));
  }
};

export const updateCapability = async (
  id: Identifier,
  data: Omit<Capability, 'id' | 'capabilityOnId'> & {
    capabilityOnId?: Identifier;
  }
) => {
  try {
    await api.put(`/api/v3/capabilities/${id}`, denormalize(data));
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Unable to update capability.'));
  }
};

export const addCapability = async (
  data: Omit<Capability, 'id' | 'capabilityOnId'> & {
    capabilityOnId?: Identifier;
  }
) => {
  try {
    const response = await api.post(`/api/v3/capabilities`, denormalize(data));
    return normalize(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Unable to add new capability.'));
  }
};
